import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import '@/App.css';
import '@/styles/responsive.css';
import ErrorBoundary from '@/components/ErrorBoundary';
import { ThemeProvider } from '@/contexts/ThemeContext';
import { Loader2 } from 'lucide-react';
import PremiumRoute from '@/components/PremiumRoute';
import LoggedInHeader from '@/components/navigation/LoggedInHeader';
import { profileAPI } from '@/lib/api';

// i18n internationalization
import '@/i18n';

// ScrollToTop component - scrolls to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Scroll to top immediately on route change
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Route validation to prevent duplicates (development only)
if (process.env.NODE_ENV === 'development') {
  const routePaths = new Set();
  const validateRoutes = () => {
    // This will be checked during development builds
    console.log('Route validation: Checking for duplicate paths...');
  };
  validateRoutes();
}

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50">
    <div className="text-center">
      <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
      <p className="text-gray-600 font-medium">Loading...</p>
    </div>
  </div>
);

// Immediate load (critical pages only - for fastest initial render)
import LandingPage from '@/pages/LandingPage';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Dashboard from '@/pages/Dashboard';
import AuthCallback from '@/components/AuthCallback';
import NotFoundPage from '@/pages/NotFoundPage';

// Lazy load all other pages (code splitting for better performance)
// Report pages
const SalaryReportsList = lazy(() => import('@/pages/SalaryReportsList'));
const SalaryReportView = lazy(() => import('@/pages/SalaryReportView'));
const InterviewPrepReportsList = lazy(() => import('@/pages/InterviewPrepReportsList'));
const InterviewPrepReportView = lazy(() => import('@/pages/InterviewPrepReportView'));
const ATSReportsList = lazy(() => import('@/pages/ATSReportsList'));
const SavedAssessments = lazy(() => import('@/pages/SavedAssessments'));
const ResumeView = lazy(() => import('@/pages/ResumeView'));

// Admin pages
const AdminAINotes = lazy(() => import('@/pages/AdminAINotesV2'));

// Template pages
const BrowseTemplates = lazy(() => import('@/pages/BrowseTemplates'));
const ChooseBuilder = lazy(() => import('@/pages/ChooseBuilder'));
const TemplatePreview = lazy(() => import('@/pages/TemplatePreview'));

// Credential & Opportunities
const CredentialSearch = lazy(() => import('@/pages/CredentialSearch'));
const SavedOpportunities = lazy(() => import('@/pages/SavedOpportunities'));

// Partnership pages
const PartnershipProgram = lazy(() => import('@/pages/PartnershipProgram'));
const PartnershipLearnMore = lazy(() => import('@/pages/PartnershipLearnMore'));
const PartnerApplication = lazy(() => import('@/pages/PartnerApplication'));
const PartnerApplicationSuccess = lazy(() => import('@/pages/PartnerApplicationSuccess'));
const PartnerDashboard = lazy(() => import('@/pages/PartnerDashboard'));

// Notifications & Legal
const Notifications = lazy(() => import('@/pages/Notifications'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const Contact = lazy(() => import('@/pages/Contact'));

// Events pages
const EventsManagement = lazy(() => import('@/pages/EventsManagement'));
const EventCreate = lazy(() => import('@/pages/EventCreate'));
const EventDetail = lazy(() => import('@/pages/EventDetail'));
const WebinarStudio = lazy(() => import('@/pages/WebinarStudio'));
const PublicEventPage = lazy(() => import('@/pages/PublicEventPage'));
const SmartFormBuilder = lazy(() => import('@/pages/SmartFormBuilder'));
const FormTemplateGallery = lazy(() => import('@/pages/FormTemplateGallery'));
const EventCheckIn = lazy(() => import('@/pages/EventCheckIn'));

// Learning pages
const CourseCatalog = lazy(() => import('@/pages/LearningPages').then(module => ({ default: module.CourseCatalog })));
const CourseDetail = lazy(() => import('@/pages/LearningPages').then(module => ({ default: module.CourseDetail })));
const LessonPlayer = lazy(() => import('@/pages/LearningPages').then(module => ({ default: module.LessonPlayer })));
const MyLearning = lazy(() => import('@/pages/LearningPages').then(module => ({ default: module.MyLearning })));
const MemberHome = lazy(() => import('@/pages/LearningPages').then(module => ({ default: module.MemberHome })));
const InstructorPage = lazy(() => import('@/pages/LearningPages').then(module => ({ default: module.InstructorPage })));

// Resume & Job Search pages (lazy loaded)
const ResumeBuilder = lazy(() => import('@/pages/ResumeBuilder'));
const ResumeUpload = lazy(() => import('@/pages/ResumeUpload'));
const ResumeHub = lazy(() => import('@/pages/ResumeHub'));
const ResumeEditor = lazy(() => import('@/pages/ResumeEditor'));
const VisualResumeBuilder = lazy(() => import('@/pages/VisualResumeBuilder'));
const CoverLetterGenerator = lazy(() => import('@/pages/CoverLetterGenerator'));
const JobSearch = lazy(() => import('@/pages/JobSearch'));
const ProfileSettings = lazy(() => import('@/pages/ProfileSettings'));
const TemplateGallery = lazy(() => import('@/pages/TemplateGallery'));
const InterviewPrep = lazy(() => import('@/pages/InterviewPrep'));
const SalaryAnalyzer = lazy(() => import('@/pages/SalaryAnalyzer'));
const JobTracker = lazy(() => import('@/pages/JobTracker'));
const JobBoardHub = lazy(() => import('@/pages/JobBoardHub'));
const RemoteWorkCenter = lazy(() => import('@/pages/RemoteWorkCenter'));
const RemoteSkillsAssessment = lazy(() => import('@/pages/RemoteSkillsAssessment'));
const RemoteInterviewPrep = lazy(() => import('@/pages/RemoteInterviewPrep'));
const RemoteResumeOptimizer = lazy(() => import('@/pages/RemoteResumeOptimizer'));
const SavedRemoteResumes = lazy(() => import('@/pages/SavedRemoteResumes'));
const SavedRemoteAssessments = lazy(() => import('@/pages/SavedRemoteAssessments'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const HelpCenter = lazy(() => import('@/pages/HelpCenter'));
const HelpArticle = lazy(() => import('@/pages/HelpArticle'));
const Support = lazy(() => import('@/pages/Support'));
const TicketManagement = lazy(() => import('@/pages/TicketManagement'));
const MyTickets = lazy(() => import('@/pages/MyTickets'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const Checkout = lazy(() => import('@/pages/Checkout'));
const CheckoutSuccess = lazy(() => import('@/pages/CheckoutSuccess'));
const SubscriptionSuccess = lazy(() => import('@/pages/SubscriptionSuccess'));
const Organizations = lazy(() => import('@/pages/Organizations'));
const OrganizationPartnerDashboard = lazy(() => import('@/pages/OrganizationPartnerDashboard'));
const OrganizationDetail = lazy(() => import('@/pages/OrganizationDetail'));
const OrganizationInvites = lazy(() => import('@/pages/OrganizationInvites'));
const MyOrganization = lazy(() => import('@/pages/MyOrganization'));
const MyOrganizationDetail = lazy(() => import('@/pages/MyOrganizationDetail'));
const UnifiedOrganizationDetail = lazy(() => import('@/pages/UnifiedOrganizationDetail'));
const ReferralSignup = lazy(() => import('@/pages/ReferralSignup'));
const SubscriptionManagement = lazy(() => import('@/pages/SubscriptionManagement'));
const AdminSubscriptions = lazy(() => import('@/pages/AdminSubscriptions'));
const AdminPricingManagement = lazy(() => import('@/pages/AdminPricingManagement'));
const AdminIntegrations = lazy(() => import('@/pages/AdminIntegrations'));
const PartnerAnalytics = lazy(() => import('@/pages/PartnerAnalytics'));
const PartnerLeaderboard = lazy(() => import('@/pages/PartnerLeaderboard'));
const PartnerAchievements = lazy(() => import('@/pages/PartnerAchievements'));
const PartnerSettings = lazy(() => import('@/pages/PartnerSettings'));
const AffiliateApplication = lazy(() => import('@/pages/AffiliateApplication'));
const AffiliateDashboard = lazy(() => import('@/pages/AffiliateDashboard'));
const AffiliateMaterials = lazy(() => import('@/pages/AffiliateMaterials'));
const AffiliateAgreement = lazy(() => import('@/pages/AffiliateAgreement'));
const AffiliateProgram = lazy(() => import('@/pages/AffiliateProgram'));
const BecomeACoach = lazy(() => import('@/pages/BecomeACoach'));
const BecomeAnOrganizationPartner = lazy(() => import('@/pages/BecomeAnOrganizationPartner'));
const OrganizationPartnerApplication = lazy(() => import('@/pages/OrganizationPartnerApplication'));
const CoachApplication = lazy(() => import('@/pages/CoachApplication'));
const CoachProgram = lazy(() => import('@/pages/CoachProgram'));
const AdminAffiliateManager = lazy(() => import('@/pages/AdminAffiliateManager'));
const AdminAffiliateContests = lazy(() => import('@/pages/AdminAffiliateContests'));
const AdminPayoutManagement = lazy(() => import('@/pages/AdminPayoutManagement'));
const FeedbackSurveysManager = lazy(() => import('@/pages/FeedbackSurveysManager'));
const FeedbackSurveyPage = lazy(() => import('@/pages/FeedbackSurveyPage'));
const WFHRemoteDatabase = lazy(() => import('@/pages/WFHRemoteDatabase'));
const WFHDirectory = lazy(() => import('@/pages/WFHDirectory'));
const AdminJobBoardHub = lazy(() => import('@/pages/AdminJobBoardHub'));
const AdminPartnerships = lazy(() => import('@/pages/AdminPartnerships'));
const AdminCustomDomains = lazy(() => import('@/pages/AdminCustomDomains'));
const AdminOrganizations = lazy(() => import('@/pages/AdminOrganizations'));
const AdminBackups = lazy(() => import('@/pages/AdminBackups'));
const CareerResources = lazy(() => import('@/pages/CareerResources'));
const CareerResourcesTools = lazy(() => import('@/pages/CareerResourcesTools'));
const CareerPlanner = lazy(() => import('@/pages/CareerPlanner'));

// Offer Engine Pages
const OfferPage = lazy(() => import('@/pages/OfferPage'));
const WorkshopPage = lazy(() => import('@/pages/WorkshopPage'));
const OfferSuccessPage = lazy(() => import('@/pages/OfferSuccessPage'));
const ExecutiveApplicationPage = lazy(() => import('@/pages/ExecutiveApplicationPage'));
const IntakeFormPage = lazy(() => import('@/pages/IntakeFormPage'));
const CoachingOffersPage = lazy(() => import('@/pages/CoachingOffersPage'));
const AdminExecutiveApplications = lazy(() => import('@/pages/AdminExecutiveApplications'));
const AdminContactSubmissions = lazy(() => import('@/pages/AdminContactSubmissions'));
const AdminWorkshopRegistrations = lazy(() => import('@/pages/admin/AdminWorkshopRegistrations'));
const WorkshopAttendanceTracking = lazy(() => import('@/pages/admin/WorkshopAttendanceTracking'));
const WorkshopAnalyticsDashboard = lazy(() => import('@/pages/admin/WorkshopAnalyticsDashboard'));
const CoachCRMDashboard = lazy(() => import('@/pages/CoachCRMDashboard'));
const StrategySessionPage = lazy(() => import('@/pages/StrategySessionPage'));
const WorkshopsTrainingPage = lazy(() => import('@/pages/WorkshopsTrainingPage'));
const WorkshopRequestPage = lazy(() => import('@/pages/WorkshopRequestPage'));
const UpcomingWorkshopsPage = lazy(() => import('@/pages/UpcomingWorkshopsPage'));
const WorkshopRegistrationPage = lazy(() => import('@/pages/WorkshopRegistrationPage'));
const TailoredServicesPage = lazy(() => import('@/pages/TailoredServicesPage'));
const WorkshopTopicDetailPage = lazy(() => import('@/pages/WorkshopTopicDetailPage'));
const ServiceDetailPage = lazy(() => import('@/pages/ServiceDetailPage'));
const BookConsultationPage = lazy(() => import('@/pages/BookConsultationPage'));

// Public Pages (About)
const AboutPage = lazy(() => import('@/pages/public/AboutPage'));
const RescheduleConsultation = lazy(() => import('@/pages/RescheduleConsultation'));
const CoachConsultationsPage = lazy(() => import('@/pages/CoachConsultationsPage'));
const CoachingPackageDetailPage = lazy(() => import('@/pages/CoachingPackageDetailPage'));
const CoachCalendarDashboard = lazy(() => import('@/pages/coach/CoachCalendarDashboard'));
const CoachAvailabilityPage = lazy(() => import('@/pages/coach/CoachAvailabilityPage'));
const EnhancedCoachDashboard = lazy(() => import('@/pages/coach/EnhancedCoachDashboard'));
const RoleBasedDashboard = lazy(() => import('@/pages/RoleBasedDashboard'));

// Checkout Pages (Guest Checkout Flow)
const GuestCheckoutPage = lazy(() => import('@/pages/checkout/GuestCheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('@/pages/checkout/CheckoutSuccessPage'));

// Progress, Email Sequences & AI Recommendations
const ClientProgressDashboard = lazy(() => import('@/pages/ClientProgressDashboard'));
const EmailSequencesManager = lazy(() => import('@/pages/EmailSequencesManager'));

// Member Self-Service Portal & Forums
const MyCohorts = lazy(() => import('@/pages/MyCohorts'));
const CohortForum = lazy(() => import('@/pages/CohortForum'));
const CohortMessages = lazy(() => import('@/pages/CohortMessages'));

// SEO Landing Pages
const CareerDevelopment = lazy(() => import('@/pages/seo/CareerDevelopment'));
const WorkforceDevelopment = lazy(() => import('@/pages/seo/WorkforceDevelopment'));
const JobSearchTools = lazy(() => import('@/pages/seo/JobSearchTools'));
const ResumeHelp = lazy(() => import('@/pages/seo/ResumeHelp'));
const GEDSupport = lazy(() => import('@/pages/seo/GEDSupport'));
const CareerCoachingPage = lazy(() => import('@/pages/seo/CareerCoaching'));

// Content Hub / Blog
const ContentHub = lazy(() => import('@/pages/ContentHub'));
const ArticleView = lazy(() => import('@/pages/ArticleView'));
const AdminContentHub = lazy(() => import('@/pages/AdminContentHub'));
const ContentCalendar = lazy(() => import('@/pages/ContentCalendar'));
const ContentAnalytics = lazy(() => import('@/pages/ContentAnalytics'));
const AuthorManagement = lazy(() => import('@/pages/AuthorManagement'));
const AuthorAnalytics = lazy(() => import('@/pages/AuthorAnalytics'));
const SocialMediaManagement = lazy(() => import('@/pages/SocialMediaManagement'));
const CommentModeration = lazy(() => import('@/pages/CommentModeration'));

const CareerPlanHub = lazy(() => import('@/pages/CareerPlanHub'));
const CareerPlanView = lazy(() => import('@/pages/CareerPlanView'));
const CoverLettersList = lazy(() => import('@/pages/CoverLettersList'));
const CoverLetterView = lazy(() => import('@/pages/CoverLetterView'));
const ResumesList = lazy(() => import('@/pages/ResumesList'));
const SavedJobsList = lazy(() => import('@/pages/SavedJobsList'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const AdminComingSoon = lazy(() => import('@/pages/AdminComingSoon'));
const AdminEmailAnalytics = lazy(() => import('@/pages/AdminEmailAnalytics'));
const AdminToolUsageAnalytics = lazy(() => import('@/pages/AdminToolUsageAnalytics'));
const AdminToolUsageComparison = lazy(() => import('@/pages/AdminToolUsageComparison'));
const AdminOrgPartnerRoles = lazy(() => import('@/pages/AdminOrgPartnerRoles'));
const AdminPilotTemplates = lazy(() => import('@/pages/AdminPilotTemplates'));
const AdminRolesDirectory = lazy(() => import('@/pages/AdminRolesDirectory'));
const AdminRoleAuditLog = lazy(() => import('@/pages/AdminRoleAuditLog'));
const AdminStaffOnboarding = lazy(() => import('@/pages/AdminStaffOnboarding'));
const AdminDiagnostics = lazy(() => import('@/pages/AdminDiagnostics'));
const AdminNotificationPreferences = lazy(() => import('@/pages/AdminNotificationPreferences'));
const AdminHiringAnalytics = lazy(() => import('@/pages/AdminHiringAnalytics'));
const AdminWebhookIntegrations = lazy(() => import('@/pages/AdminWebhookIntegrations'));
const AdminReportsExport = lazy(() => import('@/pages/AdminReportsExport'));
const WebsiteLinksDirectory = lazy(() => import('@/pages/WebsiteLinksDirectory'));
const HelpCenterAdmin = lazy(() => import('@/pages/HelpCenterAdmin'));
const CareersPage = lazy(() => import('@/pages/CareersPage'));
const ApplicationStatusPage = lazy(() => import('@/pages/ApplicationStatusPage'));
const Assessments = lazy(() => import('@/pages/Assessments'));
const TakeAssessment = lazy(() => import('@/pages/TakeAssessment'));
const AssessmentResults = lazy(() => import('@/pages/AssessmentResults'));
const ATSReport = lazy(() => import('@/pages/ATSReport'));
const ATSReportEnhanced = lazy(() => import('@/pages/ATSReportEnhanced'));
const CliptoClone = lazy(() => import('@/pages/CliptoClone'));
const ATSCheck = lazy(() => import('@/pages/ATSCheck'));
const Settings = lazy(() => import('@/pages/Settings'));
const AdminUserAccess = lazy(() => import('@/pages/AdminUserAccess'));
const AdminUsers = lazy(() => import('@/pages/AdminUsers'));
const AdminDeletedAccounts = lazy(() => import('@/pages/AdminDeletedAccounts'));
const ReportsDashboard = lazy(() => import('@/pages/admin/ReportsDashboard'));
const AdminReferralProgram = lazy(() => import('@/pages/AdminReferralProgram'));
const AdminReferralContests = lazy(() => import('@/pages/AdminReferralContests'));
const ReferralAnalytics = lazy(() => import('@/pages/ReferralAnalytics'));
const ReferralMaterials = lazy(() => import('@/pages/ReferralMaterials'));
const ShareAndEarn = lazy(() => import('@/pages/ShareAndEarn'));
const ReferralRedirect = lazy(() => import('@/pages/ReferralRedirect'));
const AffiliateRedirect = lazy(() => import('@/pages/AffiliateRedirect'));
const PartnerManagement = lazy(() => import('@/pages/PartnerManagement'));
const AllActiveCoaches = lazy(() => import('@/pages/AllActiveCoaches'));
const AllOrganizationCoaches = lazy(() => import('@/pages/AllOrganizationCoaches'));
const AdminSkillsGapAnalysis = lazy(() => import('@/pages/AdminSkillsGapAnalysis'));
const AdminProgressTracking = lazy(() => import('@/pages/AdminProgressTracking'));
const ProgressTracking = lazy(() => import('@/pages/ProgressTracking'));
const UserProgressActivityManagement = lazy(() => import('@/pages/UserProgressActivityManagement'));
const AdminLinkedInOptimizer = lazy(() => import('@/pages/AdminLinkedInOptimizer'));
const AdminNetworkingTracker = lazy(() => import('@/pages/AdminNetworkingTracker'));
const AdminSuccessStories = lazy(() => import('@/pages/AdminSuccessStories'));
const AdminCohortManagement = lazy(() => import('@/pages/AdminCohortManagement'));
const AdminOutcomeReporting = lazy(() => import('@/pages/AdminOutcomeReporting'));
const AdminGEDContentSeeder = lazy(() => import('@/pages/AdminGEDContentSeeder'));
const ApplicationManager = lazy(() => import('@/pages/ApplicationManager'));
const ClinicalSupportTool = lazy(() => import('@/pages/ClinicalSupportTool'));
const MyCoach = lazy(() => import('@/pages/MyCoach'));
const MyScheduledSessions = lazy(() => import('@/pages/MyScheduledSessions'));
const CoachDashboard = lazy(() => import('@/pages/CoachDashboard'));
const CoachProfile = lazy(() => import('@/pages/CoachProfile'));
const Messages = lazy(() => import('@/pages/Messages'));
const Sessions = lazy(() => import('@/pages/Sessions'));
const SessionHistory = lazy(() => import('@/pages/SessionHistory'));
const AllUpcomingSessions = lazy(() => import('@/pages/AllUpcomingSessions'));
const AllPastSessions = lazy(() => import('@/pages/AllPastSessions'));
const AllSessions = lazy(() => import('@/pages/AllSessions'));
const Goals = lazy(() => import('@/pages/Goals'));
const Reviews = lazy(() => import('@/pages/Reviews'));
const CoachDirectory = lazy(() => import('@/pages/CoachDirectory'));
const CoachPublicProfile = lazy(() => import('@/pages/CoachPublicProfile'));
const CoachAvailabilityView = lazy(() => import('@/pages/CoachAvailabilityView'));
const BookCoach = lazy(() => import('@/pages/BookCoach'));
const PurchaseConfirmation = lazy(() => import('@/pages/PurchaseConfirmation'));
const CoachPaymentDashboard = lazy(() => import('@/pages/CoachPaymentDashboard'));
const CoachIntakeFormsManagement = lazy(() => import('@/pages/CoachIntakeFormsManagement'));
const CoachContractsManagement = lazy(() => import('@/pages/CoachContractsManagement'));
const CoachAllClients = lazy(() => import('@/pages/CoachAllClients'));
const CoachAllConnections = lazy(() => import('@/pages/CoachAllConnections'));
const IntakeForms = lazy(() => import('@/pages/IntakeForms'));
const IntakeFormBuilder = lazy(() => import('@/pages/IntakeFormBuilder'));
const IntakeFormFill = lazy(() => import('@/pages/IntakeFormFill'));
const IntakeFormResponses = lazy(() => import('@/pages/IntakeFormResponses'));
const Contracts = lazy(() => import('@/pages/Contracts'));
const ContractEditor = lazy(() => import('@/pages/ContractEditor'));
const ContractReview = lazy(() => import('@/pages/ContractReview'));
const BookSession = lazy(() => import('@/pages/BookSession'));
const BookComplimentary = lazy(() => import('@/pages/BookComplimentary'));
const ContractSign = lazy(() => import('@/pages/ContractSign'));
const SessionConfirmed = lazy(() => import('@/pages/SessionConfirmed'));
const ConnectedMembers = lazy(() => import('@/pages/ConnectedMembers'));
const LinkedInCallback = lazy(() => import('@/pages/LinkedInCallback'));
const SplitScreenBuilder = lazy(() => import('@/pages/SplitScreenBuilder'));
const PublicResumeView = lazy(() => import('@/pages/PublicResumeView'));
const MyEvents = lazy(() => import('@/pages/MyEvents'));
const EventCalendarView = lazy(() => import('@/pages/EventCalendarView'));
// Event Reports and Webinar
const EventReports = lazy(() => import('@/pages/EventReports'));
const WebinarRoom = lazy(() => import('@/pages/WebinarRoom'));
const WebinarJoin = lazy(() => import('@/pages/WebinarJoin'));
const CreatorBranding = lazy(() => import('@/pages/CreatorBranding'));
const EvergreenPlayer = lazy(() => import('@/pages/EvergreenPlayer'));
const AttendanceAnalytics = lazy(() => import('@/pages/AttendanceAnalytics'));

// Admin Pages
const EmailAnalyticsDashboard = lazy(() => import('@/pages/EmailAnalyticsDashboard'));
const ABTestingDashboard = lazy(() => import('@/pages/ABTestingDashboard'));

// Internal Jobs & Employer System
const InternalJobs = lazy(() => import('@/pages/InternalJobs'));
const EmployerPostJob = lazy(() => import('@/pages/EmployerPostJob'));
const EmployerDashboard = lazy(() => import('@/pages/EmployerDashboard'));
const AdminJobManagement = lazy(() => import('@/pages/AdminJobManagement'));
const JobAlerts = lazy(() => import('@/pages/JobAlerts'));
const CareerIntelligence = lazy(() => import('@/pages/CareerIntelligence'));
const SkillAssessments = lazy(() => import('@/pages/SkillAssessments'));
const LearningPaths = lazy(() => import('@/pages/LearningPaths'));
const LearningResources = lazy(() => import('@/pages/LearningResources'));
const Certificates = lazy(() => import('@/pages/Certificates'));
const Achievements = lazy(() => import('@/pages/Achievements'));
const AdminAnalytics = lazy(() => import('@/pages/AdminAnalytics'));
const AdminPlatformMetrics = lazy(() => import('@/pages/AdminPlatformMetrics'));
const AdminABTesting = lazy(() => import('@/pages/AdminABTesting'));
const AdminContentHealth = lazy(() => import('@/pages/AdminContentHealth'));
const AdminContentManagement = lazy(() => import('@/pages/AdminContentManagement'));

// Learning Management System (LMS)
const AdminLearningPlatform = lazy(() => import('@/pages/AdminLearningPlatform'));
const LearningPages = lazy(() => import('@/pages/LearningPages'));

// Newsletter System
const NewsletterLanding = lazy(() => import('@/pages/NewsletterLandingDynamic'));
const NewsletterUnsubscribe = lazy(() => import('@/pages/NewsletterUnsubscribe'));
const NewsletterConfirmation = lazy(() => import('@/pages/NewsletterConfirmation'));
const NewsletterView = lazy(() => import('@/pages/NewsletterView'));
const AdminNewsletters = lazy(() => import('@/pages/AdminNewsletters'));
const NewsletterEditor = lazy(() => import('@/pages/NewsletterEditor'));
const UserNewsletterDashboard = lazy(() => import('@/pages/UserNewsletterDashboard'));
const PremiumSubscription = lazy(() => import('@/pages/PremiumSubscription'));
const NewsletterUpgrade = lazy(() => import('@/pages/NewsletterUpgrade'));
const NewsletterCheckoutSuccess = lazy(() => import('@/pages/NewsletterCheckoutSuccess'));
const AdvertisementManagement = lazy(() => import('@/pages/AdvertisementManagement'));
const AdminYouTubeIntegration = lazy(() => import('@/pages/AdminYouTubeIntegration'));
const FormRefurbisher = lazy(() => import('@/pages/FormRefurbisher'));
const AdminGPSAnalytics = lazy(() => import('@/pages/AdminGPSAnalytics'));
const AdminCareerResources = lazy(() => import('@/pages/AdminCareerResources'));
const PathwayGEDDashboard = lazy(() => import('@/pages/PathwayGEDDashboard'));
const PathwayGEDLearnMore = lazy(() => import('@/pages/PathwayGEDLearnMore'));
const PathwayGEDApplication = lazy(() => import('@/pages/PathwayGEDApplication'));
const PathwayGEDSubjectWorkspace = lazy(() => import('@/pages/PathwayGEDSubjectWorkspace'));
const PathwayGEDCareerPathway = lazy(() => import('@/pages/PathwayGEDCareerPathway'));
const PathwayGEDAIHelp = lazy(() => import('@/pages/PathwayGEDAIHelp'));
const PathwayGEDAdmin = lazy(() => import('@/pages/PathwayGEDAdmin'));
const PathwayGEDTimedTest = lazy(() => import('@/pages/PathwayGEDTimedTest'));
const PathwayGEDAchievements = lazy(() => import('@/pages/PathwayGEDAchievements'));
// const PathwayGEDCommunity = lazy(() => import('@/pages/PathwayGEDCommunity')); // Disabled - feature removed
const PathwayGEDCertificates = lazy(() => import('@/pages/PathwayGEDCertificates'));
const PathwayGEDStudyPlan = lazy(() => import('@/pages/PathwayGEDStudyPlan'));
const PathwayGEDGames = lazy(() => import('@/pages/PathwayGEDGames'));
const PathwayGEDSimulations = lazy(() => import('@/pages/PathwayGEDSimulations'));
const PathwayGEDTestingCenters = lazy(() => import('@/pages/PathwayGEDTestingCenters'));
const PathwayGEDNotifications = lazy(() => import('@/pages/PathwayGEDNotifications'));
const PathwayGEDEvents = lazy(() => import('@/pages/PathwayGEDEvents'));
const PathwayGEDHelp = lazy(() => import('@/pages/PathwayGEDHelp'));
const PathwayGEDSupport = lazy(() => import('@/pages/PathwayGEDSupport'));
const PathwayGEDMyPlan = lazy(() => import('@/pages/PathwayGEDMyPlan'));
const PathwayGEDResources = lazy(() => import('@/pages/PathwayGEDResources'));
const PathwayGEDAIPracticeTest = lazy(() => import('@/pages/PathwayGEDAIPracticeTest'));
const PathwayGEDCoaching = lazy(() => import('@/pages/PathwayGEDCoaching'));
const PathwayGEDCoachingOptions = lazy(() => import('@/pages/PathwayGEDCoachingOptions'));
const PathwayGEDCoachingPricing = lazy(() => import('@/pages/PathwayGEDCoachingPricing'));
const PathwayGEDCoachingCheckout = lazy(() => import('@/pages/PathwayGEDCoachingCheckout'));
const PathwayGEDCoachingSuccess = lazy(() => import('@/pages/PathwayGEDCoachingSuccess'));
const PathwayGEDGroupPackages = lazy(() => import('@/pages/PathwayGEDGroupPackages'));
const PathwayGEDOneOnOnePackages = lazy(() => import('@/pages/PathwayGEDOneOnOnePackages'));
const GEDPathwayManager = lazy(() => import('@/pages/GEDPathwayManager'));
const GEDCoachDirectory = lazy(() => import('@/pages/GEDCoachDirectory'));
const GEDCoachPublicProfile = lazy(() => import('@/pages/GEDCoachPublicProfile'));
const GEDCoachingBooking = lazy(() => import('@/pages/GEDCoachingBooking'));
const GEDCoachDashboard = lazy(() => import('@/pages/GEDCoachDashboard'));
const GEDStudentDashboard = lazy(() => import('@/pages/GEDStudentDashboard'));
const AdminGroupSessionScheduler = lazy(() => import('@/pages/AdminGroupSessionScheduler'));
const AdminInstructorAssignment = lazy(() => import('@/pages/AdminInstructorAssignment'));
const GEDChat = lazy(() => import('@/pages/GEDChat'));
const GEDWebinarRoom = lazy(() => import('@/pages/GEDWebinarRoom'));
const GEDInstructorPortal = lazy(() => import('@/pages/GEDInstructorPortal'));
const GEDCoachingScheduler = lazy(() => import('@/pages/GEDCoachingScheduler'));
const GEDInstructorDashboard = lazy(() => import('@/pages/GEDInstructorDashboard'));
const GEDInstructorProfile = lazy(() => import('@/pages/GEDInstructorProfile'));
const GEDInstructorDirectory = lazy(() => import('@/pages/GEDInstructorDirectory'));

// GED Student-Instructor Features
const GEDMessaging = lazy(() => import('@/pages/GEDMessaging'));
const GEDMyBookings = lazy(() => import('@/pages/GEDMyBookings'));
const GEDBookingSuccess = lazy(() => import('@/pages/GEDBookingSuccess'));
const GEDSessionRoom = lazy(() => import('@/pages/GEDSessionRoom'));
const GEDWriteReview = lazy(() => import('@/pages/GEDWriteReview'));
const AdminGEDReviews = lazy(() => import('@/pages/AdminGEDReviews'));
const AdminStudentViewPreview = lazy(() => import('@/pages/AdminStudentViewPreview'));
const GEDScheduleSession = lazy(() => import('@/pages/GEDScheduleSession'));

// Launch System Pages
const FoundersPricing = lazy(() => import('@/pages/FoundersPricing'));
const FounderUpgrade = lazy(() => import('@/pages/FounderUpgrade'));
const FounderCheckout = lazy(() => import('@/pages/FounderCheckout'));
const EarlyAdopterPricing = lazy(() => import('@/pages/EarlyAdopterPricing'));
const EarlyAdopterCheckout = lazy(() => import('@/pages/EarlyAdopterCheckout'));
const AdminLaunchSettings = lazy(() => import('@/pages/AdminLaunchSettings'));
const NotificationSettings = lazy(() => import('@/components/NotificationSettings'));
const FounderCircleLanding = lazy(() => import('@/pages/FounderCircleLanding'));
const EarlyAdoptersLanding = lazy(() => import('@/pages/EarlyAdoptersLanding'));
const VIPLanding = lazy(() => import('@/pages/VIPLanding'));
const VIPPricing = lazy(() => import('@/pages/VIPPricing'));

// Email Sequencer
const EmailPreferences = lazy(() => import('@/pages/EmailPreferences'));
const AdminEmailSequenceManager = lazy(() => import('@/pages/AdminEmailSequenceManager'));

// Tool Access Manager
const ToolAccessManager = lazy(() => import('@/pages/ToolAccessManager'));
const ToolStore = lazy(() => import('@/pages/ToolStore'));
const ToolPurchaseSuccess = lazy(() => import('@/pages/ToolPurchaseSuccess'));
const BundlePurchaseSuccess = lazy(() => import('@/pages/BundlePurchaseSuccess'));

// Monetization & Analytics
const MonetizationDashboard = lazy(() => import('@/pages/MonetizationDashboard'));
const GiftPurchaseSuccess = lazy(() => import('@/pages/GiftPurchaseSuccess'));
const CreditsPage = lazy(() => import('@/pages/CreditsPage'));

// Advanced Features
const CommunityForum = lazy(() => import('@/pages/CommunityForum'));
const AuditLogs = lazy(() => import('@/pages/AuditLogs'));
const ToolFavorites = lazy(() => import('@/pages/ToolFavorites'));
const AdminOperations = lazy(() => import('@/pages/AdminOperations'));
const ModerationQueue = lazy(() => import('@/pages/ModerationQueue'));

// GED Lesson System
const GEDLessonAdmin = lazy(() => import('@/pages/GEDLessonAdmin'));
const GEDLessonEditor = lazy(() => import('@/pages/GEDLessonEditor'));
const GEDLessonViewer = lazy(() => import('@/pages/GEDLessonViewer'));
const GEDPracticeSession = lazy(() => import('@/pages/GEDPracticeSession'));
const GEDPracticeHistory = lazy(() => import('@/pages/GEDPracticeHistory'));
const FunderDashboard = lazy(() => import('@/pages/FunderDashboard'));

import { Toaster } from '@/components/ui/sonner';
import HelpWidget from '@/components/HelpWidget';
import AIAssistant from '@/components/AIAssistant';
import GlobalAdProvider from '@/components/GlobalAdProvider';
import { AdTriggerProvider } from '@/contexts/AdTriggerContext';
import { FeedbackProvider } from '@/components/FeedbackProvider';
import { WebSocketNotificationProvider } from '@/contexts/WebSocketNotificationProvider';

// AuthenticatedLayout - wraps authenticated pages with LoggedInHeader
const AuthenticatedLayout = ({ children }) => {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Get user from localStorage first for immediate display
    const storedUser = localStorage.getItem('user');
    if (storedUser && storedUser !== 'undefined') {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Failed to parse stored user:', e);
      }
    }
    
    // Fetch fresh user data
    const fetchUser = async () => {
      try {
        const response = await profileAPI.get();
        const userData = response.data.user || response.data;
        if (userData && userData.name) {
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error);
      }
    };
    fetchUser();
  }, []);
  
  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };
  
  return (
    <div className="min-h-screen">
      <LoggedInHeader user={user} onLogout={handleLogout} />
      {children}
    </div>
  );
};

const ProtectedRoute = ({ children, skipHeader = false }) => {
  // Check if user data exists in localStorage
  // Since we moved to cookie-based auth, we check for user instead of token
  const user = localStorage.getItem('user');
  const location = window.location;
  
  if (!user) {
    // Store the intended destination so user can be redirected after login
    const returnUrl = location.pathname + location.search;
    if (returnUrl !== '/login' && returnUrl !== '/') {
      sessionStorage.setItem('returnUrl', returnUrl);
    }
    return <Navigate to="/login" replace />;
  }
  
  // If skipHeader is true (like for Dashboard which has its own), just return children
  if (skipHeader) {
    return children;
  }
  
  // Otherwise wrap with AuthenticatedLayout
  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
};

function App() {
  // Initialize CSRF token on app mount
  useEffect(() => {
    const initApp = async () => {
      const { initializeCsrf } = await import('@/utils/csrf');
      await initializeCsrf();
    };
    initApp();
  }, []);

  return (
    <HelmetProvider>
    <ThemeProvider>
      <ErrorBoundary>
        <BrowserRouter>
          <ScrollToTop />
          <AdTriggerProvider>
          <GlobalAdProvider>
          <WebSocketNotificationProvider>
          <FeedbackProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/careers" element={<Suspense fallback={<PageLoader />}><CareersPage /></Suspense>} />
              <Route path="/application-status" element={<Suspense fallback={<PageLoader />}><ApplicationStatusPage /></Suspense>} />
              <Route path="/referral/:code" element={<Suspense fallback={<PageLoader />}><ReferralRedirect /></Suspense>} />
              <Route path="/affiliate/:code" element={<Suspense fallback={<PageLoader />}><AffiliateRedirect /></Suspense>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/auth/linkedin/callback" element={<LinkedInCallback />} />
              <Route path="/join/*" element={<Suspense fallback={<PageLoader />}><ReferralSignup /></Suspense>} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute skipHeader={true}>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-orange-50 py-8 px-4">
                    <div className="max-w-2xl mx-auto">
                      <h1 className="text-2xl font-bold text-gray-900 mb-6">Notification Settings</h1>
                      <NotificationSettings />
                    </div>
                  </div>
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminUsers />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/deleted-accounts"
            element={
              <ProtectedRoute requiredRoles={['super_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AdminDeletedAccounts />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports-dashboard"
            element={
              <ProtectedRoute requiredRoles={['super_admin', 'admin']}>
                <Suspense fallback={<PageLoader />}>
                  <ReportsDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tool-access"
            element={
              <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <ToolAccessManager />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tool-store"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ToolStore />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tool-purchase-success"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ToolPurchaseSuccess />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bundle-purchase-success"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <BundlePurchaseSuccess />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/gift-purchase-success"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GiftPurchaseSuccess />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/credits"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CreditsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/monetization"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <MonetizationDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/operations"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminOperations />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/audit-logs"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AuditLogs />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/moderation"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ModerationQueue />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/coming-soon"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminComingSoon />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminEmailAnalytics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tool-usage-analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminToolUsageAnalytics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/org-partner-roles"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminOrgPartnerRoles />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pilot-templates"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminPilotTemplates />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles-directory"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminRolesDirectory />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/staff-onboarding"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminStaffOnboarding />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/role-audit-log"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminRoleAuditLog />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/diagnostics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminDiagnostics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/website-links"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <WebsiteLinksDirectory />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/help-center"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <HelpCenterAdmin />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notification-preferences"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminNotificationPreferences />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/hiring-analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminHiringAnalytics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/webhook-integrations"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminWebhookIntegrations />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/reports-export"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminReportsExport />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tool-usage-comparison"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminToolUsageComparison />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/community"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CommunityForum />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/favorites"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ToolFavorites />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/referral-program"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminReferralProgram />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/referral-contests"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminReferralContests />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/referral-analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ReferralAnalytics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/share-and-earn"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ShareAndEarn />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/user-access/:userId"
            element={
              <ProtectedRoute>
                <AdminUserAccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ai-notes"
            element={
              <ProtectedRoute>
                <AdminAINotes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/skills-gap"
            element={
              <ProtectedRoute>
                <AdminSkillsGapAnalysis />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/clinical-support"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ClinicalSupportTool />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/clinical-support-dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ClinicalSupportTool />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/progress-tracking"
            element={
              <ProtectedRoute>
                <AdminProgressTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/progress"
            element={
              <ProtectedRoute>
                <ProgressTracking />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/progress-activity"
            element={
              <ProtectedRoute>
                <UserProgressActivityManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/linkedin-optimizer"
            element={
              <ProtectedRoute>
                <AdminLinkedInOptimizer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/networking-tracker"
            element={
              <ProtectedRoute>
                <AdminNetworkingTracker />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/success-stories"
            element={
              <ProtectedRoute>
                <AdminSuccessStories />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cohorts"
            element={
              <ProtectedRoute>
                <AdminCohortManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/cohort-management"
            element={
              <ProtectedRoute>
                <AdminCohortManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-cohorts"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <MyCohorts />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-cohorts/:cohortId/forum"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CohortForum />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/cohort-messages"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CohortMessages />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/applications"
            element={
              <ProtectedRoute>
                <ApplicationManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/outcomes"
            element={
              <ProtectedRoute>
                <AdminOutcomeReporting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization-dashboard"
            element={
              <ProtectedRoute>
                <OrganizationPartnerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations"
            element={
              <ProtectedRoute>
                <Organizations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations/:orgId"
            element={
              <ProtectedRoute>
                <OrganizationDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organization-invites"
            element={
              <ProtectedRoute>
                <OrganizationInvites />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-organization"
            element={
              <ProtectedRoute>
                <MyOrganization />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-organization/:orgId"
            element={
              <ProtectedRoute>
                <MyOrganizationDetail />
              </ProtectedRoute>
            }
          />
          {/* Unified Organization Detail Routes (new) */}
          <Route
            path="/org/:orgId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <UnifiedOrganizationDetail viewMode="auto" />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/credential-search"
            element={
              <ProtectedRoute>
                <CredentialSearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved-opportunities"
            element={
              <ProtectedRoute>
                <SavedOpportunities />
              </ProtectedRoute>
            }
          />
          <Route
            path="/browse-templates"
            element={
              <ProtectedRoute skipHeader={true}>
                <PremiumRoute toolId="browse_templates">
                  <BrowseTemplates />
                </PremiumRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/choose-builder/:resumeId"
            element={
              <ProtectedRoute>
                <ChooseBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/template-preview/:resumeId"
            element={
              <ProtectedRoute>
                <TemplatePreview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/split-builder/:resumeId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <SplitScreenBuilder />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/builder/:resumeId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <SplitScreenBuilder />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/r/:shareId"
            element={
              <Suspense fallback={<PageLoader />}>
                <PublicResumeView />
              </Suspense>
            }
          />

          <Route
            path="/resume-hub"
            element={
              <ProtectedRoute>
                <ResumeHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume/builder"
            element={
              <ProtectedRoute>
                <ResumeBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume/new"
            element={
              <ProtectedRoute>
                <ResumeBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume/upload"
            element={
              <ProtectedRoute>
                <ResumeUpload />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume/paste"
            element={
              <ProtectedRoute>
                <ResumeUpload mode="paste" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume/visual-builder"
            element={
              <ProtectedRoute>
                <VisualResumeBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume/:id"
            element={
              <ProtectedRoute>
                <ResumeEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cover-letter"
            element={
              <ProtectedRoute>
                <PremiumRoute toolId="cover_letter">
                  <CoverLetterGenerator />
                </PremiumRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/jobs"
            element={<JobSearch />}
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfileSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/templates"
            element={
              <ProtectedRoute>
                <PremiumRoute toolId="browse_templates">
                  <TemplateGallery />
                </PremiumRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/interview-prep"
            element={
              <ProtectedRoute>
                <InterviewPrep />
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary-analyzer"
            element={
              <ProtectedRoute>
                <PremiumRoute toolId="salary_analyzer">
                  <SalaryAnalyzer />
                </PremiumRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/job-tracker"
            element={
              <ProtectedRoute>
                <PremiumRoute toolId="job_tracker">
                  <JobTracker />
                </PremiumRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/job-board-hub"
            element={
              <ProtectedRoute>
                <PremiumRoute toolId="job_board_hub">
                  <JobBoardHub />
                </PremiumRoute>
              </ProtectedRoute>
            }
          />
          <Route
            path="/remote-work-center"
            element={
              <ProtectedRoute>
                <RemoteWorkCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/remote-skills-assessment"
            element={
              <ProtectedRoute>
                <RemoteSkillsAssessment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/remote-interview-prep"
            element={
              <ProtectedRoute>
                <RemoteInterviewPrep />
              </ProtectedRoute>
            }
          />
          <Route
            path="/remote-resume-optimizer"
            element={
              <ProtectedRoute>
                <RemoteResumeOptimizer />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved-remote-resumes"
            element={
              <ProtectedRoute>
                <SavedRemoteResumes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved-remote-assessments"
            element={
              <ProtectedRoute>
                <SavedRemoteAssessments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resources"
            element={
              <ProtectedRoute>
                <CareerResourcesTools />
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-resources-tools"
            element={
              <ProtectedRoute>
                <CareerResourcesTools />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resources/:id"
            element={
              <ProtectedRoute>
                <CareerResources />
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-resources"
            element={
              <ProtectedRoute>
                <CareerResources />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessments"
            element={
              <ProtectedRoute>
                <Assessments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessments/:type/start"
            element={
              <ProtectedRoute>
                <TakeAssessment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/assessments/results/:id"
            element={
              <ProtectedRoute>
                <AssessmentResults />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ats-report/:id"
            element={
              <ProtectedRoute>
                <ATSReportEnhanced />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ats-report-old/:id"
            element={
              <ProtectedRoute>
                <ATSReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ats-check"
            element={
              <ProtectedRoute>
                <ATSCheck />
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-planner"
            element={
              <ProtectedRoute>
                <CareerPlanner />
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-plan-hub"
            element={
              <ProtectedRoute>
                <CareerPlanHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-plan-view/:planId"
            element={
              <ProtectedRoute>
                <CareerPlanView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cover-letters"
            element={
              <ProtectedRoute>
                <CoverLettersList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/cover-letter-view/:letterId"
            element={
              <ProtectedRoute>
                <CoverLetterView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resumes"
            element={
              <ProtectedRoute>
                <ResumesList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved-jobs"
            element={
              <ProtectedRoute>
                <SavedJobsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach-management"
            element={
              <ProtectedRoute>
                <PartnerManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/all-active-coaches"
            element={
              <ProtectedRoute>
                <AllActiveCoaches />
              </ProtectedRoute>
            }
          />
          <Route
            path="/all-marketplace-coaches"
            element={
              <ProtectedRoute>
                <AllActiveCoaches />
              </ProtectedRoute>
            }
          />
          <Route
            path="/all-organization-coaches"
            element={
              <ProtectedRoute>
                <AllOrganizationCoaches />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-coach"
            element={
              <ProtectedRoute>
                <MyCoach />
              </ProtectedRoute>
            }
          />
          {/* Redirect /my-coaches to /my-coach for consistency */}
          <Route
            path="/my-coaches"
            element={<Navigate to="/my-coach" replace />}
          />
          <Route
            path="/my-sessions"
            element={
              <ProtectedRoute>
                <MyScheduledSessions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach"
            element={
              <ProtectedRoute>
                <CoachDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach-profile"
            element={
              <ProtectedRoute>
                <CoachProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/:connectionId"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sessions"
            element={
              <ProtectedRoute>
                <Sessions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/session-history"
            element={
              <ProtectedRoute>
                <SessionHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sessions/upcoming"
            element={
              <ProtectedRoute>
                <AllUpcomingSessions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sessions/past"
            element={
              <ProtectedRoute>
                <AllPastSessions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sessions/all"
            element={
              <ProtectedRoute>
                <AllSessions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/goals"
            element={
              <ProtectedRoute>
                <Goals />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reviews"
            element={
              <ProtectedRoute>
                <Reviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reviews/:coachId"
            element={
              <ProtectedRoute>
                <Reviews />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach-directory"
            element={
              <ProtectedRoute>
                <CoachDirectory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach/:coachId"
            element={
              <ProtectedRoute>
                <CoachPublicProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach/:coachId/availability"
            element={
              <ProtectedRoute>
                <CoachAvailabilityView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/book-coach/:coachId"
            element={
              <ProtectedRoute>
                <BookCoach />
              </ProtectedRoute>
            }
          />
          <Route
            path="/book-complimentary/:coachId"
            element={
              <ProtectedRoute>
                <BookComplimentary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/purchase-confirmation"
            element={
              <ProtectedRoute>
                <PurchaseConfirmation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach-payments"
            element={
              <ProtectedRoute>
                <CoachPaymentDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach/intake-forms-management"
            element={
              <ProtectedRoute>
                <CoachIntakeFormsManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach/contracts-management"
            element={
              <ProtectedRoute>
                <CoachContractsManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach/all-clients"
            element={
              <ProtectedRoute>
                <CoachAllClients />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach/all-connections"
            element={
              <ProtectedRoute>
                <CoachAllConnections />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intake-forms"
            element={
              <ProtectedRoute>
                <IntakeForms />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intake-forms/builder/:formId"
            element={
              <ProtectedRoute>
                <IntakeFormBuilder />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intake-forms/fill/:formId"
            element={
              <ProtectedRoute>
                <IntakeFormFill />
              </ProtectedRoute>
            }
          />
          <Route
            path="/intake-forms/:formId/responses"
            element={
              <ProtectedRoute>
                <IntakeFormResponses />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contracts"
            element={
              <ProtectedRoute>
                <Contracts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contracts/editor/:contractId"
            element={
              <ProtectedRoute>
                <ContractEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contract-review/:contractId"
            element={
              <ProtectedRoute>
                <ContractReview />
              </ProtectedRoute>
            }
          />
          <Route
            path="/book-session/:packageId"
            element={
              <ProtectedRoute>
                <BookSession />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contract-sign/:contractId"
            element={
              <ProtectedRoute>
                <ContractSign />
              </ProtectedRoute>
            }
          />
          <Route
            path="/session-confirmed/:sessionId"
            element={
              <ProtectedRoute>
                <SessionConfirmed />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/connected-members"
            element={
              <ProtectedRoute>
                <ConnectedMembers />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach/members/:memberId"
            element={
              <ProtectedRoute>
                <CoachDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary-reports"
            element={
              <ProtectedRoute>
                <SalaryReportsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary-reports/:reportId"
            element={
              <ProtectedRoute>
                <SalaryReportView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/interview-prep-reports"
            element={
              <ProtectedRoute>
                <InterviewPrepReportsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/interview-prep-reports/:reportId"
            element={
              <ProtectedRoute>
                <InterviewPrepReportView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ats-reports"
            element={
              <ProtectedRoute>
                <ATSReportsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/saved-assessments"
            element={
              <ProtectedRoute>
                <SavedAssessments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/resume/view/:id"
            element={
              <ProtectedRoute>
                <ResumeView />
              </ProtectedRoute>
            }
          />
          
          {/* ==================== SEO LANDING PAGES (PUBLIC) ==================== */}
          <Route path="/career-development" element={<CareerDevelopment />} />
          <Route path="/workforce-development" element={<WorkforceDevelopment />} />
          <Route path="/job-search-tools" element={<JobSearchTools />} />
          <Route path="/resume-help" element={<ResumeHelp />} />
          <Route path="/ged-support" element={<GEDSupport />} />
          <Route path="/career-coaching" element={<CareerCoachingPage />} />
          
          {/* ==================== PUBLIC PAGES (ABOUT, CONTACT) ==================== */}
          <Route 
            path="/about" 
            element={
              <Suspense fallback={<PageLoader />}>
                <AboutPage />
              </Suspense>
            } 
          />
          
          {/* ==================== GUEST CHECKOUT FLOW ==================== */}
          <Route 
            path="/checkout/:offerId" 
            element={
              <Suspense fallback={<PageLoader />}>
                <GuestCheckoutPage />
              </Suspense>
            } 
          />
          <Route 
            path="/checkout/success" 
            element={
              <Suspense fallback={<PageLoader />}>
                <CheckoutSuccessPage />
              </Suspense>
            } 
          />
          
          {/* ==================== OFFER ENGINE / COACHING PROGRAMS ==================== */}
          <Route 
            path="/services" 
            element={
              <Suspense fallback={<PageLoader />}>
                <TailoredServicesPage />
              </Suspense>
            } 
          />
          <Route 
            path="/tailored-services" 
            element={
              <Suspense fallback={<PageLoader />}>
                <TailoredServicesPage />
              </Suspense>
            } 
          />
          <Route 
            path="/career-strategy-session" 
            element={
              <Suspense fallback={<PageLoader />}>
                <StrategySessionPage />
              </Suspense>
            } 
          />
          <Route 
            path="/strategy-session" 
            element={
              <Suspense fallback={<PageLoader />}>
                <StrategySessionPage />
              </Suspense>
            } 
          />
          <Route 
            path="/workshops-training" 
            element={
              <Suspense fallback={<PageLoader />}>
                <WorkshopsTrainingPage />
              </Suspense>
            } 
          />
          <Route 
            path="/workshops" 
            element={
              <Suspense fallback={<PageLoader />}>
                <WorkshopsTrainingPage />
              </Suspense>
            } 
          />
          <Route 
            path="/workshops/request" 
            element={
              <Suspense fallback={<PageLoader />}>
                <WorkshopRequestPage />
              </Suspense>
            } 
          />
          <Route 
            path="/workshops/upcoming" 
            element={
              <Suspense fallback={<PageLoader />}>
                <UpcomingWorkshopsPage />
              </Suspense>
            } 
          />
          <Route 
            path="/workshops/register/:workshopId" 
            element={
              <Suspense fallback={<PageLoader />}>
                <WorkshopRegistrationPage />
              </Suspense>
            } 
          />
          <Route 
            path="/workshop-topic/:slug" 
            element={
              <Suspense fallback={<PageLoader />}>
                <WorkshopTopicDetailPage />
              </Suspense>
            } 
          />
          <Route 
            path="/services/:slug" 
            element={
              <Suspense fallback={<PageLoader />}>
                <ServiceDetailPage />
              </Suspense>
            } 
          />
          <Route 
            path="/book-consultation" 
            element={
              <Suspense fallback={<PageLoader />}>
                <BookConsultationPage />
              </Suspense>
            } 
          />
          <Route 
            path="/reschedule-consultation" 
            element={
              <Suspense fallback={<PageLoader />}>
                <RescheduleConsultation />
              </Suspense>
            } 
          />
          <Route 
            path="/coaching" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CoachingOffersPage />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/offers/:slug" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <OfferPage />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/offers/:slug/apply" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ExecutiveApplicationPage />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/offers/:slug/success" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <OfferSuccessPage />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/offers/:slug/intake" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <IntakeFormPage />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/workshops/:slug" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <WorkshopPage />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/workshops/:slug/success" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <OfferSuccessPage />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/executive-applications" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminExecutiveApplications />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/contact-submissions" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminContactSubmissions />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/workshop-registrations" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminWorkshopRegistrations />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/workshop-attendance/:eventId" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <WorkshopAttendanceTracking />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/workshop-analytics" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <WorkshopAnalyticsDashboard />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/coach/crm" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CoachCRMDashboard />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/coach/consultations" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CoachConsultationsPage />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/coach/calendar" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CoachCalendarDashboard />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/coach/availability" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CoachAvailabilityPage />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/coach/analytics" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <EnhancedCoachDashboard />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/progress" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ClientProgressDashboard />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/progress/:userId" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ClientProgressDashboard />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin/email-sequences" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <EmailSequencesManager />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/coaching/:packageId" 
            element={
              <Suspense fallback={<PageLoader />}>
                <CoachingPackageDetailPage />
              </Suspense>
            } 
          />
          <Route 
            path="/my-portal" 
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <RoleBasedDashboard />
                </Suspense>
              </ProtectedRoute>
            } 
          />
          
          {/* ==================== CONTENT HUB / BLOG (PUBLIC) ==================== */}
          <Route 
            path="/blog" 
            element={
              <Suspense fallback={<PageLoader />}>
                <ContentHub />
              </Suspense>
            } 
          />
          <Route 
            path="/blog/:slug" 
            element={
              <Suspense fallback={<PageLoader />}>
                <ArticleView />
              </Suspense>
            } 
          />
          <Route
            path="/admin/content-hub"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminContentHub />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/content-calendar"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ContentCalendar />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/content-analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <ContentAnalytics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/authors"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AuthorManagement />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/social-media"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <SocialMediaManagement />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/author-analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AuthorAnalytics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/author-analytics/:authorId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AuthorAnalytics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/comment-moderation"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CommentModeration />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/faq"
            element={
              <ProtectedRoute>
                <FAQ />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <HelpCenter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help/:articleId"
            element={
              <ProtectedRoute>
                <HelpArticle />
              </ProtectedRoute>
            }
          />
          <Route
            path="/support"
            element={
              <ProtectedRoute>
                <Support />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/tickets"
            element={
              <ProtectedRoute>
                <TicketManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-tickets"
            element={
              <ProtectedRoute>
                <MyTickets />
              </ProtectedRoute>
            }
          />
          {/* Main site pricing page */}
          <Route
            path="/pricing"
            element={
              <Suspense fallback={<PageLoader />}>
                <Pricing />
              </Suspense>
            }
          />
          <Route
            path="/checkout"
            element={
              <ProtectedRoute>
                <Checkout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/checkout-success"
            element={
              <ProtectedRoute>
                <CheckoutSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription/success"
            element={
              <ProtectedRoute>
                <SubscriptionSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account/subscription"
            element={
              <ProtectedRoute>
                <SubscriptionManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/subscriptions"
            element={
              <ProtectedRoute>
                <AdminSubscriptions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pricing"
            element={
              <ProtectedRoute>
                <AdminPricingManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/integrations"
            element={
              <ProtectedRoute>
                <AdminIntegrations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/affiliate-apply"
            element={
              <ProtectedRoute>
                <AffiliateApplication />
              </ProtectedRoute>
            }
          />
          <Route
            path="/affiliate-dashboard"
            element={
              <ProtectedRoute>
                <AffiliateDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/affiliate-materials"
            element={
              <ProtectedRoute>
                <AffiliateMaterials />
              </ProtectedRoute>
            }
          />
          <Route
            path="/referral-materials"
            element={
              <ProtectedRoute>
                <ReferralMaterials />
              </ProtectedRoute>
            }
          />
          <Route
            path="/affiliate-agreement"
            element={
              <ProtectedRoute>
                <AffiliateAgreement />
              </ProtectedRoute>
            }
          />
          <Route path="/affiliate-program" element={<AffiliateProgram />} />
          <Route path="/become-a-coach" element={<BecomeACoach />} />
          <Route path="/become-an-organization-partner" element={<BecomeAnOrganizationPartner />} />
          <Route
            path="/organization-partner-apply"
            element={
              <ProtectedRoute>
                <OrganizationPartnerApplication />
              </ProtectedRoute>
            }
          />
          <Route path="/coach-program" element={<CoachProgram />} />
          {/* Partnership Program Routes */}
          <Route path="/partnership-program" element={<PartnershipProgram />} />
          <Route path="/partnership-learn-more" element={<PartnershipLearnMore />} />
          <Route
            path="/partner-apply"
            element={
              <ProtectedRoute>
                <PartnerApplication />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner-application-success"
            element={
              <ProtectedRoute>
                <PartnerApplicationSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner-dashboard"
            element={
              <ProtectedRoute>
                <PartnerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner-analytics"
            element={
              <ProtectedRoute>
                <PartnerAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner-leaderboard"
            element={
              <ProtectedRoute>
                <PartnerLeaderboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner-achievements"
            element={
              <ProtectedRoute>
                <PartnerAchievements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/partner-settings"
            element={
              <ProtectedRoute>
                <PartnerSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/coach-apply"
            element={
              <ProtectedRoute>
                <CoachApplication />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/affiliates"
            element={
              <ProtectedRoute>
                <AdminAffiliateManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/affiliate-manager"
            element={
              <ProtectedRoute>
                <AdminAffiliateManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/affiliate-contests"
            element={
              <ProtectedRoute>
                <AdminAffiliateContests />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/payout-management"
            element={
              <ProtectedRoute>
                <AdminPayoutManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/feedback-surveys"
            element={
              <ProtectedRoute>
                <FeedbackSurveysManager />
              </ProtectedRoute>
            }
          />
          {/* User-facing survey page - public route for taking surveys */}
          <Route
            path="/feedback/:templateId"
            element={
              <Suspense fallback={<PageLoader />}>
                <FeedbackSurveyPage />
              </Suspense>
            }
          />
          <Route
            path="/admin/wfh-remote-directory"
            element={
              <ProtectedRoute>
                <WFHRemoteDatabase />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wfh-database"
            element={
              <ProtectedRoute>
                <WFHRemoteDatabase />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wfh-directory"
            element={
              <PremiumRoute toolId="wfh_remote_directory">
                <Suspense fallback={<PageLoader />}>
                  <WFHDirectory />
                </Suspense>
              </PremiumRoute>
            }
          />
          <Route
            path="/wfh-directory/shared/:shareCode"
            element={
              <PremiumRoute toolId="wfh_remote_directory">
                <Suspense fallback={<PageLoader />}>
                  <WFHDirectory />
                </Suspense>
              </PremiumRoute>
            }
          />
          <Route
            path="/admin/job-board-hub"
            element={
              <ProtectedRoute>
                <AdminJobBoardHub />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/partnerships"
            element={
              <ProtectedRoute>
                <AdminPartnerships />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/custom-domains"
            element={
              <ProtectedRoute>
                <AdminCustomDomains />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/organizations"
            element={
              <ProtectedRoute>
                <AdminOrganizations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/backups"
            element={
              <ProtectedRoute>
                <AdminBackups />
              </ProtectedRoute>
            }
          />
          {/* Legal & Info Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/contact" element={<Contact />} />
          
          {/* Events & Webinars */}
          <Route path="/events" element={<ProtectedRoute><EventsManagement /></ProtectedRoute>} />
          <Route path="/events/create" element={<ProtectedRoute><EventCreate /></ProtectedRoute>} />
          <Route path="/events/:eventId" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
          <Route path="/events/:eventId/edit" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
          <Route path="/events/:eventId/form-builder" element={<ProtectedRoute><SmartFormBuilder /></ProtectedRoute>} />
          <Route path="/events/:eventId/templates" element={<ProtectedRoute><FormTemplateGallery /></ProtectedRoute>} />
          <Route path="/events/:eventId/check-in" element={<ProtectedRoute><EventCheckIn /></ProtectedRoute>} />
          <Route path="/events/:eventId/reports" element={<ProtectedRoute><EventReports /></ProtectedRoute>} />
          <Route path="/events/:eventId/webinar" element={<ProtectedRoute><WebinarRoom /></ProtectedRoute>} />
          <Route path="/events/:eventId/studio" element={<ProtectedRoute><WebinarStudio /></ProtectedRoute>} />
          <Route path="/events/:eventId/analytics" element={<ProtectedRoute><AttendanceAnalytics /></ProtectedRoute>} />
          <Route path="/events/branding" element={<ProtectedRoute><CreatorBranding /></ProtectedRoute>} />
          <Route path="/my-events" element={<ProtectedRoute><MyEvents /></ProtectedRoute>} />
          <Route path="/event-calendar" element={<ProtectedRoute><EventCalendarView /></ProtectedRoute>} />
          <Route path="/event/:slug" element={<PublicEventPage />} />
          <Route path="/event/:creatorSlug/:eventSlug" element={<PublicEventPage />} />
          <Route path="/webinar/:eventId/join" element={<WebinarJoin />} />
          <Route path="/watch/:eventId" element={<EvergreenPlayer />} />
          
          {/* Admin Analytics */}
          <Route path="/admin/email-analytics" element={<ProtectedRoute><EmailAnalyticsDashboard /></ProtectedRoute>} />
          <Route path="/admin/ab-testing" element={<ProtectedRoute><ABTestingDashboard /></ProtectedRoute>} />
          
          {/* Internal Jobs & Employer System */}
          <Route path="/internal-jobs" element={<Suspense fallback={<PageLoader />}><InternalJobs /></Suspense>} />
          <Route
            path="/employer/post-job"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <EmployerPostJob />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/employer/dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <EmployerDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/job-management"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminJobManagement />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/job-alerts"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <JobAlerts />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/career-intelligence"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CareerIntelligence />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/skill-assessments"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <SkillAssessments />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/learning-paths"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <LearningPaths />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/learning-resources"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <LearningResources />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/certificates"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <Certificates />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/achievements"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <Achievements />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminAnalytics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/platform-metrics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminPlatformMetrics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/content-ab-testing"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminABTesting />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/content-health"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminContentHealth />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/content-management"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminContentManagement />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Learning Management System (LMS) - Admin Only for now */}
          <Route
            path="/admin/learning"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminLearningPlatform />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Newsletter System */}
          <Route
            path="/newsletter"
            element={
              <Suspense fallback={<PageLoader />}>
                <NewsletterLanding />
              </Suspense>
            }
          />
          <Route
            path="/newsletter-confirmation"
            element={
              <Suspense fallback={<PageLoader />}>
                <NewsletterConfirmation />
              </Suspense>
            }
          />
          <Route
            path="/newsletter/view/:id"
            element={
              <Suspense fallback={<PageLoader />}>
                <NewsletterView />
              </Suspense>
            }
          />
          <Route
            path="/newsletter/unsubscribe"
            element={
              <Suspense fallback={<PageLoader />}>
                <NewsletterUnsubscribe />
              </Suspense>
            }
          />
          {/* Legacy routes - redirect to new URLs */}
          <Route path="/newsletters" element={<Navigate to="/newsletter" replace />} />
          <Route path="/newsletters/unsubscribe" element={<Navigate to="/newsletter/unsubscribe" replace />} />
          
          {/* Newsletter Upgrade Page (Public) */}
          <Route
            path="/newsletter/upgrade"
            element={
              <Suspense fallback={<PageLoader />}>
                <NewsletterUpgrade />
              </Suspense>
            }
          />
          
          {/* Newsletter Checkout Success Page (Public) */}
          <Route
            path="/newsletter/checkout/success"
            element={
              <Suspense fallback={<PageLoader />}>
                <NewsletterCheckoutSuccess />
              </Suspense>
            }
          />
          
          <Route
            path="/admin/newsletters"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminNewsletters />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/newsletters/edit/:id"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <NewsletterEditor />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/newsletters/new"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <NewsletterEditor />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Advertisement Management - Super Admin Only */}
          <Route
            path="/admin/advertisements"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdvertisementManagement />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* YouTube Integration - Admin Only */}
          <Route
            path="/admin/youtube"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminYouTubeIntegration />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Form Refurbisher - Super Admin Only */}
          <Route
            path="/admin/form-refurbisher"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <FormRefurbisher />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Clipto Clone - Transcription Studio (Super Admin) */}
          <Route
            path="/admin/tools/clipto"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CliptoClone />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GPS Analytics - Super Admin Only */}
          <Route
            path="/admin/gps-analytics"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminGPSAnalytics />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/career-resources"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminCareerResources />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Pathway GED Routes */}
          <Route
            path="/pathway-ged"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/learn-more"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDLearnMore />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/help"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDHelp />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/support"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDSupport />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/my-plan"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDMyPlan />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/apply"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDApplication />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/subject/:subject"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDSubjectWorkspace />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* GED Lesson System Routes */}
          <Route
            path="/admin/ged-lessons"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDLessonAdmin />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ged-lessons/:lessonId/edit"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDLessonEditor />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ged-lessons/new"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDLessonEditor />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/ged-content-seeder"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminGEDContentSeeder />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/lesson/:lessonId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDLessonViewer />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/practice/:practiceSetId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDPracticeSession />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/practice-history"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDPracticeHistory />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/career-pathway"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDCareerPathway />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/funder-dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <FunderDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/ai-help"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDAIHelp />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pathway-ged"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDAdmin />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/admin"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDAdmin />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/timed-test"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDTimedTest />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/achievements"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDAchievements />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* GED Community & Leaderboard - Feature removed from roadmap
          <Route
            path="/pathway-ged/community"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDCommunity />
                </Suspense>
              </ProtectedRoute>
            }
          />
          */}
          <Route
            path="/pathway-ged/certificates"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDCertificates />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/study-plan"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDStudyPlan />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/games"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDGames />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/simulations"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDSimulations />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/simulations/:simId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDSimulations />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/testing-centers"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDTestingCenters />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/notifications"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDNotifications />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/events"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDEvents />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/resources"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDResources />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/ai-practice-test"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDAIPracticeTest />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Consolidated coaching page - both routes point to merged page */}
          <Route
            path="/pathway-ged/coaching"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDCoachingOptions />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/coaching-options"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDCoachingOptions />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/coaching-pricing"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDCoachingPricing />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/coaching-checkout"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDCoachingCheckout />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/coaching-success"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDCoachingSuccess />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/group-packages"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDGroupPackages />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/one-on-one-packages"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PathwayGEDOneOnOnePackages />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/coaches"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDCoachDirectory />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/coach/:coachId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDCoachPublicProfile />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/coaching/book/:coachId/:packageId?"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDCoachingBooking />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/coach-dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDCoachDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/my-progress"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDStudentDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/pathway-ged/chat/:threadId?"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDChat />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pathway-ged/manager"
            element={
              <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <GEDPathwayManager />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pathway-ged/group-sessions"
            element={
              <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AdminGroupSessionScheduler />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Webinar Room */}
          <Route
            path="/pathway-ged/webinar/:eventId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDWebinarRoom />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Instructor Portal */}
          <Route
            path="/pathway-ged/instructor-portal"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDInstructorPortal />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Instructor Dashboard - Main dashboard for GED instructors */}
          <Route
            path="/ged-instructor-dashboard"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDInstructorDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Instructor Profile - Full profile management page */}
          <Route
            path="/ged-instructor-profile"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDInstructorProfile />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Instructor Directory (Public) */}
          <Route
            path="/ged-instructors"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDInstructorDirectory />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Messaging */}
          <Route
            path="/ged-messages"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDMessaging />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED My Bookings */}
          <Route
            path="/ged-my-bookings"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDMyBookings />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Booking Success */}
          <Route
            path="/ged-booking-success"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDBookingSuccess />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Session Room (Video Call) */}
          <Route
            path="/ged-session-room/:roomId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDSessionRoom />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Write Review */}
          <Route
            path="/ged-write-review/:instructorId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDWriteReview />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Admin: GED Review Moderation */}
          <Route
            path="/admin/ged-reviews"
            element={
              <ProtectedRoute requiredRoles={['admin', 'super_admin', 'ged_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AdminGEDReviews />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Admin: Student View Preview */}
          <Route
            path="/admin/student-view"
            element={
              <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AdminStudentViewPreview />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Student Tools Dashboard (for GED students with premium access) */}
          <Route
            path="/ged-student-tools"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <AdminStudentViewPreview />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Schedule Session */}
          <Route
            path="/ged-schedule-session/:bookingId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDScheduleSession />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* GED Coaching Scheduler */}
          <Route
            path="/pathway-ged/scheduler"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <GEDCoachingScheduler />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Admin: Instructor Assignment */}
          <Route
            path="/admin/pathway-ged/instructor-assignment"
            element={
              <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AdminInstructorAssignment />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Launch System Routes */}
          {/* Public Landing Pages - No Auth Required */}
          <Route
            path="/founder-circle"
            element={
              <Suspense fallback={<PageLoader />}>
                <FounderCircleLanding />
              </Suspense>
            }
          />
          <Route
            path="/early-adopters"
            element={
              <Suspense fallback={<PageLoader />}>
                <EarlyAdoptersLanding />
              </Suspense>
            }
          />
          <Route
            path="/vip"
            element={
              <Suspense fallback={<PageLoader />}>
                <VIPLanding />
              </Suspense>
            }
          />
          <Route
            path="/vip-pricing"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <VIPPricing />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          {/* Founders Circle pricing - site membership program */}
          <Route
            path="/founders-pricing"
            element={
              <Suspense fallback={<PageLoader />}>
                <FoundersPricing />
              </Suspense>
            }
          />
          {/* Founder Checkout - no trial, direct payment */}
          <Route
            path="/founder-checkout"
            element={
              <Suspense fallback={<PageLoader />}>
                <FounderCheckout />
              </Suspense>
            }
          />
          {/* Early Adopter pricing - site membership program */}
          <Route
            path="/early-adopter-pricing"
            element={
              <Suspense fallback={<PageLoader />}>
                <EarlyAdopterPricing />
              </Suspense>
            }
          />
          {/* Early Adopter Checkout - no trial, direct payment */}
          <Route
            path="/early-adopter-checkout"
            element={
              <Suspense fallback={<PageLoader />}>
                <EarlyAdopterCheckout />
              </Suspense>
            }
          />
          <Route
            path="/email-preferences"
            element={
              <Suspense fallback={<PageLoader />}>
                <EmailPreferences />
              </Suspense>
            }
          />
          <Route
            path="/founder-upgrade"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <FounderUpgrade />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/launch-settings"
            element={
              <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AdminLaunchSettings />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/email-sequence-manager"
            element={
              <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                <Suspense fallback={<PageLoader />}>
                  <AdminEmailSequenceManager />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/dashboard/newsletter"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <UserNewsletterDashboard />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/newsletter/premium"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <PremiumSubscription />
                </Suspense>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/learn"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CourseCatalog />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/course/:courseId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CourseDetail />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/learn/lesson/:lessonId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <LessonPlayer />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-learning"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <MyLearning />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/learning/courses/:courseId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <CourseDetail />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/member-home"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <MemberHome />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/instructor/:instructorId"
            element={
              <ProtectedRoute>
                <Suspense fallback={<PageLoader />}>
                  <InstructorPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* 404 Catch-all Route - Must be last */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
          </Suspense>
          </FeedbackProvider>
          </WebSocketNotificationProvider>
          </GlobalAdProvider>
          </AdTriggerProvider>
          <HelpWidget />
          <AIAssistant pageType="general" />
        </BrowserRouter>
      </ErrorBoundary>
      <Toaster position="top-right" />
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
